import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

/**
 * Translates a whole message object for use in things like menus where all of
 * the values are needed.
 *
 * @example
 * function YourComponent() {
 *  const translatedItems = useTranslatedMessages(menuItems);
 *  // Returns an object that looks like this in English:
 *  // { red: 'red', green: 'green', blue: 'blue' }
 * }
 *
 * const menuItems = defineMessages({
 *   red: {
 *     defaultMessage: 'red', id: '', description: 'example menu item',
 *   },
 *   green: {
 *     defaultMessage: 'green', id: '', description: 'example menu item',
 *   },
 *   blue: {
 *     defaultMessage: 'blue', id: '', description: 'example menu item',
 *   },
 * });
 *
 */
export function useTranslatedMessages<
  T extends Record<string, MessageDescriptor>,
>(messageObject: T): Record<keyof T, string> {
  const intl = useIntl();

  return React.useMemo(() => {
    const entries = Object.entries(messageObject).map(([key, descriptor]) => [
      key,
      intl.formatMessage(descriptor),
    ]);
    return Object.fromEntries(entries);
  }, [intl, messageObject]);
}
